import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import useWindowSize from '@hooks/use-window-size';
import useQueryTab, { QueryTab, ROUTER_QUERY_KEY } from '@hooks/orders-sales/use-query-tab';
import Button from '@components/atoms/button/button';
import ProductsView from '@components/orders-sales/products';
import PopinView from '@components/orders-sales/popin';
import useSaleProductShipping from '@hooks/orders-sales/use-sales-product-shipping-query';
import { useRouter } from 'next/router';
import useUser from '@hooks/user/use-user';
import { SearchProvider } from '@context/search.context';
import PageHeader from '@components/organisms/page-header/page-header';
import { URL_QUERIES } from '@constants/orders-sales';
import { TimelineProvider } from '@context/timeline.context';
import useToast from '@hooks/orders-sales/use-toast';
import ToastMessage from '@components/atoms/toast-message/toast-message';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { getCurrentProduct, mainPageId, updateURLQueries } from './helper';
import { Popin, State } from './type';
import styles from './main.module.scss';
import ShippingReminder from './product-timeline/shipping-reminder/shipping-reminder';
import ProductTimelineView from './product-timeline';

const View: React.FC = () => {
  const { t } = useTranslation(['orders-sales', 'default-layout']);
  const { tab: currentTab } = useQueryTab();
  const { isWiderThanMd } = useWindowSize();
  const [popin, updatePopin] = useState<Popin | null>(null);

  const breadcrumbs = [
    {
      localizedName: t('default-layout:HEADER.NAVIGATION.HOME'),
      url: {
        path: '/',
      },
    },
    {
      localizedName: t('MY_ACCOUNT.ORDERS_SALES.TITLE'),
      url: {
        path: '/orders-sales',
      },
    },
  ];

  const initState: State = {
    currentTab,
    shippingProduct: null, // Used for Sales to add this product at the top, when user click on Send my X sold products
    orderProduct: null,
    saleProduct: null,
    loading: false,
  };

  const router = useRouter();

  const { user } = useUser();

  const [state, updateState] = useState<State>(initState);

  const { toast, removeToast, showToast } = useToast();

  const updateSectionQuery = useCallback(
    (tab: QueryTab) => {
      switch (tab) {
        case 'Order':
          updateURLQueries(router.pathname, { section: ROUTER_QUERY_KEY.order });
          break;
        case 'Sale':
          updateURLQueries(router.pathname, { section: ROUTER_QUERY_KEY.sale });
          break;
        default:
          updateURLQueries(router.pathname);
          break;
      }
    },
    [router.pathname],
  );

  useEffect(() => {
    const section = new URLSearchParams(window.location.search).get(URL_QUERIES.section);

    if (user && !section) {
      const tab = user.segment === 'C2C' ? 'Order' : 'Sale';

      updateState((prev) => ({
        ...prev,
        currentTab: tab,
      }));
      updateSectionQuery(tab);
    }
  }, [user, updateSectionQuery]);

  const product = getCurrentProduct(state);
  const { productId } = product || {};

  const isMobileDetail = !isWiderThanMd && product;

  const { data } = useSaleProductShipping();

  const { updateDataLayer } = useAnalyticEvents('orders_sales_timeline');

  useEffect(() => {
    if (productId) {
      updateDataLayer({
        product_id: productId,
      });
    }
    // no need updateDataLayer as dependency
    // eslint-disable-next-line
  }, [productId]);

  const saleReports = data?.saleReports?.sort((reportA, reportB) => (reportA.year <= reportB.year ? 1 : -1));
  const saleReport = saleReports && saleReports.length ? saleReports[0].filePath : '';

  return (
    <TimelineProvider
      state={state}
      updateState={updateState}
      updatePopin={updatePopin}
      toast={toast}
      removeToast={removeToast}
      showToast={showToast}
    >
      <main
        className={styles.ordersSales}
        id={mainPageId}
      >
        <div className={styles.ordersSales__container}>
          <PageHeader
            title={t('MY_ACCOUNT.ORDERS_SALES.TITLE')}
            breadcrumbs={breadcrumbs}
            className={styles.ordersSales__pageHeader}
          />

          {!isMobileDetail && (
            <div className={styles.ordersSales__header}>
              {saleReport != null && saleReport !== '' && (
                <Button
                  onClick={() => {
                    updatePopin({
                      kind: 'SaleReport',
                      rootSaleReport: saleReport,
                      saleReports: saleReports,
                    });
                  }}
                  className={styles.ordersSales__link}
                >
                  {t('MY_ACCOUNT.ORDERS_SALES.LIST.TAX_SOCIAL_OBLI')}
                </Button>
              )}
            </div>
          )}

          <ShippingReminder data={data} />

          <div className={styles.ordersSales__main}>
            <SearchProvider>
              <ProductsView
                state={state}
                updateState={updateState}
              />
            </SearchProvider>

            <ProductTimelineView />
          </div>

          <PopinView
            popin={popin}
            closePopin={() => updatePopin(null)}
          />
        </div>
      </main>

      {!!toast && (
        <ToastMessage
          className={styles.ordersSales__toast}
          id="common-orders-sales-toast"
          title={toast.title}
          show={true}
          duration={8000}
          icon={toast.icon}
          type={toast.type}
          onClose={removeToast}
        />
      )}
    </TimelineProvider>
  );
};

export default View;
